import React, { useEffect } from 'react';
import { Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import { useUser } from './WebSocketContext';

/**
 * ProtectedRoute Component
 * 
 * Wraps routes that require specific permissions (admin/editor).
 * Redirects view_only users to the home-denied page.
 */
const ProtectedRoute = ({ children, requireAdmin = false }) => {
    const { isReadOnly, isAdmin, mfaSetupRequired } = useUser();
    const navigate = useNavigate();
    const location = useLocation();
    const isSettingsRoute = location.pathname.toLowerCase() === '/settings';

    useEffect(() => {
        if (isReadOnly && !(mfaSetupRequired && isSettingsRoute)) {
            navigate('/home-denied', { replace: true });
        }
    }, [isReadOnly, navigate, mfaSetupRequired, isSettingsRoute]);

    // If route requires admin and user is not admin, show access denied
    if (requireAdmin && !isAdmin) {
        return (
            <Box
                display="flex"
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
                height="100vh"
                sx={{ backgroundColor: '#f5f5f5', p: 3 }}
            >
                <Typography variant="h5" sx={{ mb: 2, color: '#d32f2f', fontWeight: 600 }}>
                    Access Denied
                </Typography>
                <Typography variant="body1" sx={{ color: '#666', textAlign: 'center', maxWidth: 500 }}>
                    You do not have permission to access this page. This page requires administrator privileges.
                </Typography>
            </Box>
        );
    }

    // If user is read-only (viewer), they will be redirected by useEffect
    if (isReadOnly && !(mfaSetupRequired && isSettingsRoute)) {
        return null;
    }

    // User has appropriate permissions, render the protected content
    return children;
};

export default ProtectedRoute;
